import {
  GroupSecurity,
  getCreateReferenceId,
  getReferenceId,
} from '@quromedical/fhir-common';
import type { ClaimGetResponse } from '@quromedical/models';
import { first } from '@quromedical/utils';
import { ConfirmationButton } from 'components/input';
import {
  ClaimResponseCard,
  type ClaimTemplate,
  ClaimTemplateCard,
  type InitialClaimTemplate,
} from 'core/claim';
import { Section, Skeleton, SkeletonProvider } from 'design-system';
import { useUserSession } from 'hooks/useUserSession';
import { chargeItemFetcher } from 'integration';
import type React from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import { strings } from 'strings';

interface ScreenComponentProps {
  responseData: {
    data?: ClaimGetResponse;
    isValidating: boolean;
    error: boolean;
  };
  onSubmit: () => Promise<void>;
  onEdit: (result: ClaimTemplate) => Promise<void>;
}

export const ScreenComponent: React.FC<ScreenComponentProps> = ({
  responseData,
  onSubmit,
  onEdit,
}) => {
  const session = useUserSession();

  const { data, isValidating, error } = responseData;

  const canEdit =
    session.hasAny([GroupSecurity.ClaimUpdate]) && data?.status === 'draft';

  if (error) {
    return <NotFoundScreen />;
  }

  const loading = isValidating || !data;

  const status =
    data?.responses && data?.responses.length > 0
      ? first(data?.responses)?.status
      : data?.status;

  const initial: InitialClaimTemplate | undefined = data
    ? {
        type: data.type,
        status: status,
        invoiceNumber: data.invoiceNumber,
        claimLines:
          data.chargeLines?.map((line) => ({
            chargeItemDisplay: line.chargeItem.display || line.chargeLine.code,
            chargeItemIdentifier: getCreateReferenceId(line.chargeItem),
            quantity: line.quantity,
            unitPrice: line.chargeLine.amount,
          })) || [],
        patientId: getReferenceId(data?.patient),
      }
    : undefined;

  const title = `${data?.patient?.display}`;

  const isDraft = data?.status === 'draft';

  return (
    <ScrollView>
      <SkeletonProvider loading={loading}>
        <Section unsetZIndex>
          <Skeleton>
            <ClaimTemplateCard
              title={title}
              chargeItemFetcher={chargeItemFetcher}
              canEdit={canEdit}
              initial={initial}
              onSubmit={onEdit}
              showPrice
            />
          </Skeleton>
        </Section>
        <Section isVisible={isDraft}>
          <ConfirmationButton
            buttonText={strings.ButtonTextSubmit}
            message={strings.ClaimSubmitMessage}
            title={strings.ClaimSubmitTitle}
            onSubmit={onSubmit}
            status="accent-green"
          />
        </Section>

        {data
          ? data.responses.map((res) => (
              <Section key={res.id}>
                <ClaimResponseCard claimResponse={res} claim={data} />
              </Section>
            ))
          : null}
      </SkeletonProvider>
    </ScrollView>
  );
};
